// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 688ee883-ee4a-446c-abf3-22eabddf9467

// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

// ---------------------------------------------------------------------------
// Primordia — guided walkthrough engine (first-run product tour)
// ---------------------------------------------------------------------------
// A self-contained coach-mark tour: a full-screen spotlight built with the
// classic box-shadow "hole" technique (an absolutely-positioned transparent
// rect over the target, with a huge rgba box-shadow that shades everything
// else — no clip-path, no SVG mask) plus an MUI Popper balloon anchored to
// the spotlighted element. No new dependency: Popper already ships inside
// @mui/material, which is how the whole repo stays dependency-light.
//
// A step's target is resolved through a PRIORITIZED selector list — a
// data-tour attribute first (this app does not have any yet; see the
// integration report for where to add them), then aria-label, then a
// visible-text match, with a raw CSS selector as a last-resort escape hatch
// for elements with neither (the Inventory search box, matched on its
// placeholder). A step whose target cannot be found on the CURRENT screen is
// skipped silently and the tour moves on — screens differ by role (a client
// user has no Master overview tile; a master's default landing view has no
// Inventory search box), so "not found" is an expected outcome, not an error.
//
// Persistence (localStorage key 'primordia.tour.v1') lives in ./tourState —
// this component only calls it, so the host app's mount point stays a single
// <Tour steps={...} open={...} onClose={...} onFinish={...} /> with no extra
// wiring for "did the user finish or skip".
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import {
  Box, Paper, Popper, Typography, Button, IconButton, Checkbox, FormControlLabel, Stack,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { markTourCompleted, markTourSkipped } from './tourState';

// Marks the tour's OWN DOM (blocker, spotlight, balloon) so the visible-text
// fallback can never accidentally target a piece of the tour itself — e.g. a
// step whose text happens to match a word inside the balloon's own body copy.
const OVERLAY_ATTR = 'data-tour-overlay';
const PAD = 8; // spotlight padding around the target's own box, in px
const RADIUS = 10;

function isVisible(el) {
  if (!el || !(el instanceof Element)) return false;
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return false;
  let style;
  try {
    style = window.getComputedStyle(el);
  } catch (e) {
    return true; // non-browser/test env — don't let a defensive check hide the target
  }
  if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
  return true;
}

function normalizeText(s) {
  return (s || '').replace(/\s+/g, ' ').trim().toLowerCase();
}

// Interactive-only scope covers real controls (buttons, links, the MUI base
// classes every button/tab/chip renders through). The broad scope adds plain
// text containers for steps that target a label or KPI rather than a control
// (e.g. the "Active clients" tile on the master overview has no button).
const INTERACTIVE_SELECTOR = 'button, a, [role="button"], input, select, .MuiButtonBase-root, .MuiTab-root, .MuiChip-root';
const BROAD_SELECTOR = `${INTERACTIVE_SELECTOR}, h1, h2, h3, h4, h5, h6, span, div, p, label, li, td, th`;

function queryByText(value, interactiveOnly) {
  const norm = normalizeText(value);
  if (!norm) return null;
  const scope = interactiveOnly ? INTERACTIVE_SELECTOR : BROAD_SELECTOR;
  const nodes = document.querySelectorAll(scope);
  let exact = null;
  let loose = null;
  let looseLen = Infinity;
  for (let i = 0; i < nodes.length; i += 1) {
    const node = nodes[i];
    if (node.closest(`[${OVERLAY_ATTR}]`)) continue; // never target the tour's own UI
    const text = normalizeText(node.textContent);
    if (!text) continue;
    if (text === norm) { exact = node; break; }
    // Loose fallback: substring match, preferring the SHORTEST matching node
    // (most specific — a button whose only text is the target, not a whole
    // toolbar that happens to contain it somewhere in its subtree).
    if (text.includes(norm) && text.length < looseLen) { loose = node; looseLen = text.length; }
  }
  return exact || loose;
}

function cssEscape(value) {
  return window.CSS && CSS.escape ? CSS.escape(value) : value;
}

function resolveSelector(sel) {
  try {
    if (sel.type === 'data-tour') return document.querySelector(`[data-tour="${cssEscape(sel.value)}"]`);
    if (sel.type === 'aria-label') return document.querySelector(`[aria-label="${cssEscape(sel.value)}"]`);
    if (sel.type === 'text') return queryByText(sel.value, sel.interactiveOnly !== false);
    if (sel.type === 'css') return document.querySelector(sel.value);
  } catch (e) {
    return null;
  }
  return null;
}

// Tries a step's selectors in the order given; returns the first match that
// is actually visible on screen right now (present-but-hidden, e.g. behind an
// unopened menu, does not count — that step is skipped like a missing one).
function resolveStepTarget(step) {
  const selectors = step.selectors || [];
  for (let i = 0; i < selectors.length; i += 1) {
    const el = resolveSelector(selectors[i]);
    if (el && isVisible(el)) return el;
  }
  return null;
}

const PLACEMENT_MAP = {
  top: 'top', bottom: 'bottom', left: 'left', right: 'right',
  'top-start': 'top-start', 'bottom-start': 'bottom-start',
};

export default function Tour({ steps, open, onClose, onFinish }) {
  const theme = useTheme();
  const [idx, setIdx] = useState(0);
  const [targetEl, setTargetEl] = useState(null);
  const [rect, setRect] = useState(null);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const list = Array.isArray(steps) ? steps : [];
  const step = list[idx] || null;

  // Every (re-)open starts clean at step 0, regardless of where a previous
  // run left off.
  useEffect(() => {
    if (open) { setIdx(0); setDontShowAgain(false); }
  }, [open]);

  const closeTour = useCallback((persist) => {
    if (persist) markTourSkipped();
    setTargetEl(null);
    setRect(null);
    if (onClose) onClose();
  }, [onClose]);

  const finishTour = useCallback(() => {
    markTourCompleted();
    setTargetEl(null);
    setRect(null);
    if (onFinish) onFinish();
  }, [onFinish]);

  // Resolve the current step's target. A step that cannot be resolved on the
  // current screen is skipped forward silently rather than shown pointed at
  // nothing; running off the end of the list this way still counts as
  // finishing (there was nothing left to show), not skipping.
  useLayoutEffect(() => {
    if (!open || !step) return;
    const el = resolveStepTarget(step);
    if (!el) {
      if (idx < list.length - 1) setIdx((i) => i + 1);
      else finishTour();
      return;
    }
    setTargetEl(el);
    if (el.scrollIntoView) {
      const r = el.getBoundingClientRect();
      const offscreen = r.top < 60 || r.bottom > window.innerHeight - 60;
      if (offscreen) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
    // Re-resolving is driven entirely by (open, idx, step identity) — steps
    // is expected to be a stable array from tourSteps.js, so this does not
    // need `list` or `finishTour` in its own dependency list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idx, step]);

  // Track the resolved element's box, recomputed on resize/scroll — capture
  // phase, since the scrolling container is very often not the window itself
  // (a facet rail, a dialog, a sticky drawer) — and once more shortly after
  // any smooth scrollIntoView above has had time to settle.
  useEffect(() => {
    if (!targetEl) return undefined;
    const update = () => setRect(targetEl.getBoundingClientRect());
    update();
    const t = setTimeout(update, 260);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [targetEl]);

  // Escape = skip the whole tour, from whichever step it's on.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeTour(dontShowAgain);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, dontShowAgain, closeTour]);

  if (!open || !step || !targetEl || !rect) return null;

  const isLast = idx === list.length - 1;
  const isFirst = idx === 0;
  const placement = PLACEMENT_MAP[step.placement] || 'bottom';

  const goNext = () => {
    if (isLast) finishTour();
    else setIdx((i) => Math.min(i + 1, list.length - 1));
  };
  const goBack = () => setIdx((i) => Math.max(i - 1, 0));

  return (
    <>
      {/* Layer 1 — full-viewport click-blocker. Fully transparent; its only
          job is pointer-events choreography: the rest of the page is inert
          while the tour is open, and only the balloon (layer 3, its own
          Popper stacking context) receives clicks. */}
      <Box
        data-tour-overlay="blocker"
        sx={{ position: 'fixed', inset: 0, zIndex: theme.zIndex.tooltip + 100, bgcolor: 'transparent' }}
        onClick={(e) => e.stopPropagation()}
      />
      {/* Layer 2 — the spotlight. A small transparent rect sized to the
          target's own box (plus PAD), whose oversized rgba box-shadow paints
          the dark shade across the ENTIRE rest of the viewport — the "hole"
          falls out of the box-shadow spread, nothing is actually cut. Purely
          visual: pointerEvents none so it never fights layer 1's blocking,
          and a themed border supplies the highlight ring. */}
      <Box
        data-tour-overlay="spotlight"
        sx={{
          position: 'fixed',
          top: rect.top - PAD,
          left: rect.left - PAD,
          width: rect.width + PAD * 2,
          height: rect.height + PAD * 2,
          borderRadius: `${RADIUS}px`,
          boxShadow: '0 0 0 9999px rgba(2, 6, 16, 0.72)',
          border: '2px solid',
          borderColor: 'sectionHeader.main',
          transition: 'top 0.2s ease, left 0.2s ease, width 0.2s ease, height 0.2s ease',
          pointerEvents: 'none',
          zIndex: theme.zIndex.tooltip + 101,
        }}
      />
      <Popper
        open
        anchorEl={targetEl}
        placement={placement}
        sx={{ zIndex: theme.zIndex.tooltip + 102 }}
        modifiers={[
          { name: 'offset', options: { offset: [0, 14] } },
          { name: 'preventOverflow', options: { boundary: 'viewport', padding: 12 } },
          { name: 'flip', enabled: true },
        ]}
      >
        <Paper
          data-tour-overlay="balloon"
          role="dialog"
          aria-modal="true"
          aria-label={step.title}
          elevation={8}
          sx={{
            p: 2.25, width: 340, maxWidth: '90vw',
            bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
          }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
            <Typography sx={{ fontWeight: 700, color: 'sectionHeader.main', fontSize: 15, pr: 1 }}>
              {step.title}
            </Typography>
            <IconButton size="small" aria-label="Skip tour" onClick={() => closeTour(dontShowAgain)} sx={{ mt: -0.5, mr: -0.5 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.55, mt: 0.75 }}>
            {step.body}
          </Typography>

          {/* Step dots — one per authored step, not per step actually shown,
              so the count matches what the copy in tourSteps.js promises. */}
          {list.length > 1 && (
            <Stack direction="row" spacing={0.6} sx={{ mt: 1.75, mb: 0.5 }}>
              {list.map((s, i) => (
                <Box
                  key={s.id}
                  sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: i === idx ? 'primary.main' : 'divider' }}
                />
              ))}
            </Stack>
          )}

          <FormControlLabel
            sx={{ mt: 0.5, ml: -0.5 }}
            control={(
              <Checkbox size="small" checked={dontShowAgain} onChange={(e) => setDontShowAgain(e.target.checked)} />
            )}
            label={<Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>Don&rsquo;t show this again</Typography>}
          />

          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
            <Button size="small" onClick={() => closeTour(dontShowAgain)} sx={{ color: 'text.secondary' }}>
              Skip tour
            </Button>
            <Stack direction="row" spacing={1}>
              {!isFirst && <Button size="small" onClick={goBack}>Back</Button>}
              <Button size="small" variant="contained" onClick={goNext}>
                {isLast ? 'Done' : 'Next'}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Popper>
    </>
  );
}
